import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus, Users } from "lucide-react";
import { QuickAddCustomerDialog } from "./QuickAddCustomerDialog";

const digits = (s) => String(s || "").replace(/\D/g, "");

const haystack = (c) => {
  const contacts = (c.contacts || []).flatMap((p) => [p.name, p.email, p.phone, p.title]);
  return [c.name, c.email, c.phone, c.address, ...contacts].filter(Boolean).join(" ").toLowerCase();
};

export const searchCustomers = (customers, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return customers;
  const terms = q.split(/\s+/);
  const qDigits = digits(q);
  return customers.filter((c) => {
    const text = haystack(c);
    const phones = [c.phone, ...(c.contacts || []).map((p) => p.phone)].map(digits).join(" ");
    return terms.every((t) => text.includes(t)) || (qDigits.length >= 3 && phones.includes(qDigits));
  });
};

const subline = (c) => [c.email, c.phone].filter(Boolean).join(" · ");

export const CustomerCombobox = ({ customers, value, onChange, onCreated, allowAll = false, allowCreate = false, placeholder = "Select customer", testId = "customer-select", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const selected = customers.find((c) => c.id === value);
  const results = useMemo(() => searchCustomers(customers, query), [customers, query]);

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  const startAdd = () => {
    setOpen(false);
    setAdding(true);
  };

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} data-testid={testId} className={`w-full justify-between font-normal h-10 px-3 ${className}`}>
            {selected ? (
              <span className="truncate text-left" data-testid={`${testId}-value`}>
                <span className="font-medium">{selected.name}</span>
                {subline(selected) && <span className="hidden sm:inline text-xs text-slate-500 ml-2">{subline(selected)}</span>}
              </span>
            ) : (
              <span className="text-slate-500 truncate">{allowAll && !value ? "All Customers" : placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start" data-testid={`${testId}-popover`}>
          <Command shouldFilter={false}>
            <CommandInput value={query} onValueChange={setQuery} placeholder="Search name, email, phone, address…" data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty data-testid={`${testId}-empty`}>
                <p className="text-slate-500">No customers match "{query}"</p>
                {allowCreate && (
                  <Button type="button" size="sm" variant="outline" className="mt-3" onClick={startAdd} data-testid={`${testId}-add-new-empty`}>
                    <Plus className="w-4 h-4 mr-1" /> Add new customer
                  </Button>
                )}
              </CommandEmpty>
              {allowAll && !query && (
                <CommandGroup>
                  <CommandItem value="__all__" onSelect={() => pick("")} data-testid={`${testId}-option-all`}>
                    <Users className="h-4 w-4 text-slate-500" />
                    <span>All Customers</span>
                    {!value && <Check className="ml-auto h-4 w-4" />}
                  </CommandItem>
                </CommandGroup>
              )}
              {results.length > 0 && (
                <CommandGroup heading={query ? `${results.length} match${results.length === 1 ? "" : "es"}` : `${customers.length} customers`}>
                  {results.map((c) => (
                    <CommandItem key={c.id} value={c.id} onSelect={() => pick(c.id)} data-testid={`${testId}-option`} className="items-start py-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{c.name}</p>
                        {subline(c) && <p className="text-xs text-slate-500 truncate">{subline(c)}</p>}
                        {c.address && <p className="text-xs text-slate-400 truncate">{c.address}</p>}
                      </div>
                      {c.id === value && <Check className="h-4 w-4 mt-1 text-indigo-600" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {allowCreate && results.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem value="__add_new__" onSelect={startAdd} className="text-indigo-700" data-testid={`${testId}-add-new`}>
                      <Plus className="h-4 w-4" /> Add new customer
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {allowCreate && (
        <QuickAddCustomerDialog
          open={adding}
          onOpenChange={setAdding}
          initialName={query}
          testId={testId}
          onCreated={(c) => { onCreated && onCreated(c); pick(c.id); }}
        />
      )}
    </>
  );
};
